import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { MONTHS, YEARS, money, todayParts } from "../../constants";
import {
  Badge,
  Button,
  Icon,
  DataCard,
  Input,
  Modal,
  PageTitle,
  Select,
  Spinner,
  Stat,
  Td,
  Th
} from "../../components/ui";
import { KunciPayrollBar, useKunciPayroll } from "../salary/KunciPayroll";

// ─── Month name → numeric index ───────────────────────────────────────────────
function monthToNum(monthName) {
  return MONTHS.indexOf(monthName) + 1; // 1-12
}

export default function SalaryAdjustment({ setToast, isAdmin = false, isSuperAdmin = false }) {
  const today = todayParts();

  // Month/Year filter — bound to local state
  const [filters, setFilters] = useState({
    year: today.year,
    month: today.month   // e.g. "May"
  });

  // Kunci payroll BERSAMA (trainer + staff) untuk bulan yang sedang dilihat.
  const bulanNum = monthToNum(filters.month);
  const { kunci, muat: muatKunci } = useKunciPayroll(Number(filters.year), bulanNum);
  const terkunci = !!kunci?.terkunci;

  const [rows, setRows]       = useState([]);
  const [rawRows, setRawRows] = useState([]); // trainer_salary rows
  const [settings, setSettings] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null);

  // ── Load final salary + raw trainer_salary for the selected month/year ──
  async function load() {
    setLoading(true);
    try {
      const monthNum = monthToNum(filters.month);

      const [finalRes, salaryRes, settingsRes] = await Promise.all([
        api.getFinalSalaryData({ year: filters.year, month: filters.month }),
        api.getSalaryAdjustments({
          month: String(monthNum),
          year: filters.year
        }),
        api.getSystemSettings().catch(() => null)
      ]);

      if (finalRes.success) setRows(finalRes.data || []);
      else setToast({ type: "error", message: finalRes.message || "Failed to load salary" });

      if (salaryRes.success) setRawRows(salaryRes.data || []);

      setSettings(settingsRes);
    } catch (err) {
      setToast({ type: "error", message: err.message || "Failed to load" });
    } finally {
      setLoading(false);
    }
  }

  // Re-fetch whenever month or year changes
  useEffect(() => { load(); }, [filters.month, filters.year]);

  // ── Ekspor rekap gaji ──────────────────────────────────────
  //
  // Dibangun dari data yang SUDAH tampil, bukan permintaan baru ke server — berkas
  // yang diunduh dijamin sama dengan yang dilihat admin. Kalau ia menarik ulang,
  // hitung ulang di server bisa menggeser angkanya di antara dua permintaan dan
  // admin memegang berkas yang tak pernah ia lihat di layar.
  //
  // SATU lembar: ringkasan per orang, yaitu angka yang dibayarkan. Rincian per
  // sesi sengaja tak ikut — ia sudah ada di tabel bawah halaman ini, dan yang
  // dibawa keluar untuk pembayaran cukup angka akhirnya.
  async function exportExcel() {
    if (!rows.length) {
      setToast({ type: "error", message: "Tak ada data untuk diunduh." });
      return;
    }
    try {
      const ExcelJS = (await import("exceljs")).default;
      const wb = new ExcelJS.Workbook();
      const periode = `${filters.month} ${filters.year}`;
      const RP = '"Rp" #,##0';

      const NAVY = "FF0D59F2";
      const judulLembar = (ws, teks, lebarKolom) => {
        ws.mergeCells(1, 1, 1, lebarKolom);
        const c = ws.getCell(1, 1);
        c.value = teks;
        c.font = { bold: true, size: 14, color: { argb: NAVY } };
        c.alignment = { vertical: "middle" };
        ws.getRow(1).height = 24;
      };
      const kepala = (ws, judul) => {
        const r = ws.addRow(judul);
        r.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
          cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
          cell.border = { bottom: { style: "thin", color: { argb: "FFBFD0F5" } } };
        });
        r.height = 22;
        return r;
      };

      // ── Lembar 1: ringkasan per orang ────────────────────────────
      const ws = wb.addWorksheet("Ringkasan");
      judulLembar(ws, `Rekap Gaji — ${periode}${terkunci ? " · TERKUNCI" : ""}`, 9);
      ws.addRow([]);
      kepala(ws, [
        "Nama", "Jabatan", "Gaji Kelas", "Gaji Sesi Staff", "Total Shift",
        "Insentif", "Potongan", "Take Home Pay", "Catatan"
      ]);

      rows.forEach((r) => {
        const baris = ws.addRow([
          r.name,
          r.position,
          Number(r.baseKelas || 0),
          Number(r.baseStaff || 0),
          Number(r.base || 0),
          Number(r.incentives || 0),
          Number(r.deductions || 0),
          Number(r.net || 0),
          // Nol karena tarif belum diatur BUKAN hasil perhitungan. Di layar ia
          // dibedakan dengan tegas; berkas yang diunduh tak boleh kehilangan
          // pembedaan itu, sebab justru berkas inilah yang jadi dasar pembayaran.
          // Bulan terkunci: angkanya beku sejak dikunci, jadi tarif yang KEMUDIAN
          // dihapus dari Data Settings tak boleh membuatnya tampak belum diatur.
          r.tarifBelumDiatur && !terkunci ? "TARIF BELUM DIATUR" : terkunci ? "Terkunci" : (r.status || "")
        ]);
        [3, 4, 5, 6, 7, 8].forEach((k) => { baris.getCell(k).numFmt = RP; });
        baris.getCell(8).font = { bold: true };
        if (r.tarifBelumDiatur && !terkunci) {
          baris.getCell(9).font = { bold: true, color: { argb: "FFDC2626" } };
        }
      });

      const jml = (ambil) => rows.reduce((a, r) => a + Number(ambil(r) || 0), 0);
      ws.addRow([]);
      const total = ws.addRow([
        "TOTAL", "",
        jml((r) => r.baseKelas), jml((r) => r.baseStaff), jml((r) => r.base),
        jml((r) => r.incentives), jml((r) => r.deductions), jml((r) => r.net), ""
      ]);
      total.eachCell((cell, k) => {
        cell.font = { bold: true };
        cell.border = { top: { style: "double", color: { argb: NAVY } } };
        if (k >= 3 && k <= 8) cell.numFmt = RP;
      });

      ws.columns = [
        { width: 26 }, { width: 24 }, { width: 15 }, { width: 16 }, { width: 15 },
        { width: 14 }, { width: 14 }, { width: 17 }, { width: 22 }
      ];
      // Kepala tabel tetap terlihat saat digulir — daftar gaji delapan cabang
      // panjangnya bisa ratusan baris.
      ws.views = [{ state: "frozen", ySplit: 3 }];

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rekap Gaji ${periode}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal mengunduh" });
    }
  }

  return (
    <>
      <PageTitle
        title="Salary Adjustments"
        subtitle={`Viewing data for ${filters.month} ${filters.year}. Click incentives or deductions to manage records.`}
        action={
          <div className="flex gap-2">
            <Select
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: e.target.value })}
              className="w-28"
            >
              {YEARS.map((y) => <option key={y}>{y}</option>)}
            </Select>
            <Select
              value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: e.target.value })}
              className="w-40"
            >
              {MONTHS.map((m) => <option key={m}>{m}</option>)}
            </Select>
            <Button
              onClick={exportExcel}
              disabled={loading || !rows.length}
              title="Unduh rekap gaji bulan ini (ringkasan + rincian per sesi)"
            >
              <Icon name="download" className="text-base" />
              Excel
            </Button>
          </div>
        }
      />

      <KunciPayrollBar
        kunci={kunci}
        year={Number(filters.year)}
        month={bulanNum}
        bisaKunci={isAdmin}
        bisaBuka={isSuperAdmin}
        setToast={setToast}
        onBerubah={() => {
          muatKunci();
          load();
        }}
      />

      {loading ? (
        <Spinner />
      ) : (
        <>
          {/* Summary stats row */}
          {rows.length > 0 && (
            <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              <Stat
                label="Total Trainers"
                value={rows.length}
                primary
              />
              <Stat
                label="Total Base Pay"
                value={money(rows.reduce((s, r) => s + r.base, 0))}
              />
              <Stat
                label="Total Incentives"
                value={money(rows.reduce((s, r) => s + r.incentives, 0))}
                good
              />
              <Stat
                label="Total Deductions"
                value={money(rows.reduce((s, r) => s + r.deductions, 0))}
                bad
              />
              <Stat
                label="Total Net Pay"
                value={money(rows.reduce((s, r) => s + r.net, 0))}
                primary
              />
            </div>
          )}

          {/* Main table */}
          <DataCard>
            <table className="w-full min-w-[640px] whitespace-nowrap text-left">
              <thead>
                <tr>
                  {[
                    "TRAINER / STAFF",
                    "BASE",
                    "INCENTIVES",
                    "DEDUCTIONS",
                    "NET PAY",
                    "STATUS"
                  ].map((h) => (
                    <Th key={h} right={h !== "TRAINER / STAFF"}>
                      {h}
                    </Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.name}
                    className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition"
                  >
                    <Td>
                      <p className="font-black">{row.name}</p>
                      <p className="text-xs text-slate-500">{row.position}</p>
                    </Td>

                    {/* Rp 0 karena tarifnya belum diatur BUKAN hasil perhitungan — orangnya
                        mengajar, cabang ini cuma belum punya tarif untuk bracket itu. Kalau
                        ditampilkan sebagai angka biasa, ia tak bisa dibedakan dari nol yang
                        sah dan slip gaji bisa terbit dengan orang yang tak dibayar. */}
                    <Td right>
                      {row.tarifBelumDiatur && !terkunci ? (
                        <span
                          className="inline-block rounded-lg bg-red-500/15 px-2 py-1 text-xs font-black text-red-600 dark:text-red-400"
                          title={`Cabang ini belum punya tarif untuk bracket "${row.position}". Tambahkan di Data Settings → Salary Ranges, atau ganti bracket-nya di Trainer Payroll.`}
                        >
                          Tarif belum diatur
                        </span>
                      ) : (
                        <>
                          {money(row.base)}
                          {/* Satu baris kini bisa memuat gaji kelas DAN gaji sesi
                              staff. Tanpa keterangan ini, angka gabungan tak bisa
                              ditelusuri tanpa menggulir ke tabel rincian. */}
                          {Number(row.baseStaff || 0) > 0 && (
                            <p className="mt-0.5 text-[10px] font-bold text-slate-500">
                              {Number(row.baseKelas || 0) > 0
                                ? `kelas ${money(row.baseKelas)} + staff ${money(row.baseStaff)}`
                                : "dari sesi staff"}
                            </p>
                          )}
                        </>
                      )}
                    </Td>

                    <Td right>
                      <button
                        className="font-black text-green-500 hover:underline"
                        onClick={() => setModal({ row, type: "incentive", filters })}
                      >
                        {money(row.incentives)}
                      </button>
                    </Td>

                    <Td right>
                      <button
                        className="font-black text-red-500 hover:underline"
                        onClick={() => setModal({ row, type: "deduction", filters })}
                      >
                        {money(row.deductions)}
                      </button>
                    </Td>

                    <Td right className="font-black text-primary">
                      {money(row.net)}
                    </Td>

                    <Td right>
                      <StatusBadge status={terkunci ? "Terkunci" : row.status} />
                    </Td>
                  </tr>
                ))}

                {!rows.length && (
                  <tr>
                    <Td colSpan="6" className="text-center py-10 text-slate-400">
                      No finalized salary records for {filters.month} {filters.year}.
                    </Td>
                  </tr>
                )}
              </tbody>
            </table>
          </DataCard>

          {/* Raw shift earnings detail */}
          {rawRows.length > 0 && (
            <div className="mt-6">
              <h3 className="mb-3 text-lg font-black">
                Shift Earnings Breakdown — {filters.month} {filters.year}
              </h3>
              <DataCard>
                <table className="w-full min-w-[520px] whitespace-nowrap text-left">
                  <thead>
                    <tr>
                      <Th>Trainer / Staff</Th>
                      <Th>Session</Th>
                      <Th>Position</Th>
                      <Th right>Total Shift</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rawRows.map((r, i) => (
                      <tr key={i} className="border-t border-slate-200 dark:border-slate-800">
                        <Td>
                          <p className="font-black">{r.name}</p>
                        </Td>
                        <Td>
                          {r.session}
                          {r.jenis === "staff" && (
                            <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-black text-primary">
                              sesi staff
                            </span>
                          )}
                        </Td>
                        <Td>{r.position}</Td>
                        <Td right className="font-black text-primary">
                          {money(r.total)}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </DataCard>
            </div>
          )}
        </>
      )}

      {/* Transaction modal */}
      <TransactionModal
        open={!!modal}
        data={modal}
        filters={filters}
        settings={settings}
        onClose={() => setModal(null)}
        onSaved={load}
        setToast={setToast}
        terkunci={terkunci}
      />
    </>
  );
}

function StatusBadge({ status }) {
  const color =
    status === "Terkunci"
      ? "bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900"
      : status === "Paid"
      ? "bg-green-500/15 text-green-600"
      : status === "Draft"
      ? "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
      : "bg-primary/10 text-primary";

  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-black ${color}`}>
      {status || "Draft"}
    </span>
  );
}

// ─── Transaction Modal ────────────────────────────────────────────────────────
function TransactionModal({
  open, data, filters, settings, onClose, onSaved, setToast, terkunci = false
}) {
  const [txRows, setTxRows] = useState([]);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    category: "",
    amount: ""
  });

  useEffect(() => {
    if (open && data) fetchTx();
  }, [open, data]);

  async function fetchTx() {
    try {
      const res = await api.getTrainerTransactions({
        year: filters.year,
        month: filters.month,
        trainer: data.row.name,
        type: data.type
      });
      if (res.success) setTxRows(res.data || []);
    } catch {
      setTxRows([]);
    }
  }

  const categories =
    data?.type === "incentive"
      ? settings?.incentives || []
      : settings?.deductions || [];

  async function save() {
    if (!form.date || !form.category || Number(form.amount) <= 0) {
      setToast({ type: "error", message: "Please fill all fields." });
      return;
    }
    try {
      const res = await api.addSalaryTransaction({
        year: filters.year,
        month: filters.month,
        trainer: data.row.name,
        type: data.type,
        date: form.date,
        category: form.category,
        amount: Number(form.amount)
      });
      setToast({
        type: res.success ? "success" : "error",
        message: res.message || "Saved"
      });
      if (res.success) {
        setForm({ ...form, amount: "" });
        fetchTx();
        onSaved();
      }
    } catch (err) {
      setToast({ type: "error", message: err.message });
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this transaction?")) return;
    try {
      const res = await api.deleteSalaryTransaction(id);
      setToast({
        type: res.success ? "success" : "error",
        message: res.message || "Deleted"
      });
      if (res.success) {
        fetchTx();
        onSaved();
      }
    } catch (err) {
      setToast({ type: "error", message: err.message });
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${data?.type === "incentive" ? "Incentive" : "Deduction"} Details`}
    >
      <div className="mb-4 text-sm text-slate-500">
        Staff:{" "}
        <b className="text-primary">{data?.row?.name}</b>{" "}
        &mdash; {filters.month} {filters.year}
      </div>

      <div className="mb-5 overflow-x-auto">
      <table className="w-full min-w-[420px] whitespace-nowrap text-left">
        <thead>
          <tr>
            <Th>Date</Th>
            <Th>Category</Th>
            <Th right>Amount</Th>
            <Th right>Action</Th>
          </tr>
        </thead>
        <tbody>
          {txRows.length ? (
            txRows.map((row, i) => (
              <tr key={i} className="border-t border-slate-200 dark:border-slate-800">
                <Td>{fmtDate(row.date)}</Td>
                <Td>{row.category}</Td>
                <Td right>{money(row.amount)}</Td>
                <Td right>
                  {terkunci ? (
                    <span className="text-xs text-slate-400">—</span>
                  ) : (
                    <button
                      className="font-black text-red-500 hover:underline"
                      onClick={() => remove(row.row)}
                    >
                      Delete
                    </button>
                  )}
                </Td>
              </tr>
            ))
          ) : (
            <tr>
              <Td colSpan="4" className="text-center text-slate-400">No records found.</Td>
            </tr>
          )}
        </tbody>
      </table>
      </div>

      {/* Bulan terkunci: rinciannya tetap bisa dilihat, tapi tak bisa diubah —
          server menolaknya juga, ini supaya admin tak mengisi form yang pasti ditolak. */}
      {terkunci ? (
        <p className="rounded-xl bg-slate-100 p-3 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          <Icon name="lock" className="mr-1 align-middle text-sm" />
          Bulan ini sudah dikunci — insentif & potongan tak bisa diubah lagi.
        </p>
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-3">
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
            <Select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              <option value="">Select category…</option>
              {categories.map((c) => (
                <option key={c.category} value={c.category}>{c.category}</option>
              ))}
            </Select>
            <Input
              type="number"
              placeholder="Amount"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          </div>

          <Button className="mt-4 w-full" onClick={save}>
            Save Transaction
          </Button>
        </>
      )}
    </Modal>
  );
}

function fmtDate(val) {
  if (!val) return "";
  const d = new Date(val);
  return isNaN(d.getTime()) ? val : d.toLocaleDateString("en-GB");
}
